const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const asRate = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export const fmt = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-IN") : v ?? "—";

export const formatLakh = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  return fmt(n);
};

const FUNNEL_BAR_CLASS = {
  Generated: "",
  "Not Applied": "orange",
  Failed: "red",
  Requested: "blue",
};

export const buildApaarGaugeStyle = (rate = 0) => {
  const pct = clamp(rate, 0, 100);
  const color = pct >= 90 ? "#10b981" : pct >= 80 ? "#f59e0b" : "#ef4444";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${pct}%, rgba(245,158,11,.14) ${pct}% 100%)`,
    "--apx-gauge-color": color,
  };
};

export const buildApaarHeroHeadline = (overview = {}) => {
  const rate = overview.generation_rate ?? 0;
  if (rate >= 85) {
    return "APAAR generation is strong, but pending volume is still material.";
  }
  if (rate >= 70) {
    return "APAAR generation is progressing, but pending and consent gaps need focused closure.";
  }
  return "APAAR generation needs urgent acceleration across schools in the selected scope.";
};

export const buildApaarHeroNarrative = (overview = {}) => {
  const generated = formatLakh(overview.total_generated ?? 0);
  return `${generated} students already have APAAR generated. The next executive focus should be closing consent gaps, failed records and the small requested-case backlog.`;
};

export const buildApaarStatusChips = (overview = {}) => {
  const rate = overview.generation_rate ?? 0;
  const chips = [];
  if (rate >= 80) {
    chips.push({ label: "On-Track Generation", className: "green" });
  } else {
    chips.push({ label: "Generation Below Target", className: "" });
  }
  chips.push({
    label: `${formatLakh(overview.total_pending ?? 0)} Pending Workload`,
    className: "",
  });
  chips.push({
    label: `${fmt(overview.total_schools ?? 0)} Schools Monitored`,
    className: "blue",
  });
  return chips;
};

export const buildApaarPrimaryAction = (overview = {}, riskData = {}) => {
  const critical = riskData?.critical_schools ?? 0;
  const failures = riskData?.high_failure_schools ?? 0;
  const consent = riskData?.consent_gap_schools ?? 0;
  return `Prioritize ${critical} critical schools, ${failures} high-failure schools and ${consent} consent-gap schools for same-week follow-up.`;
};

export const buildApaarExecutiveKpis = (overview = {}) => {
  const rate = overview.generation_rate ?? 0;
  return [
    {
      label: "Total Students",
      value: fmt(overview.total_students),
      sub: "Base across selected scope",
      cardClass: "",
    },
    {
      label: "APAAR Generated",
      value: fmt(overview.total_generated),
      sub: `${rate}% generation rate`,
      cardClass: "",
    },
    {
      label: "Pending",
      value: fmt(overview.total_pending),
      sub: `${overview.pending_pct ?? 0}% to be resolved`,
      cardClass: "warn",
    },
    {
      label: "Failed Records",
      value: fmt(overview.total_failed),
      sub: `${overview.failure_rate_per_1000 ?? 0} per 1,000 students`,
      cardClass: "danger",
    },
  ];
};

export const buildApaarPriorityCards = (riskData = {}) => [
  {
    id: "critical",
    label: "Critical Schools",
    value: fmt(riskData.critical_schools ?? 0),
    body: "Schools with generation below 70% and student strength above 200.",
    cta: "Open school list →",
    cardClass: "",
    tab: "schools",
  },
  {
    id: "failure",
    label: "High Failure Schools",
    value: fmt(riskData.high_failure_schools ?? 0),
    body: "Schools having more than 50 failed APAAR records needing data correction.",
    cta: "Review failures →",
    cardClass: "orange",
    tab: "schools",
  },
  {
    id: "consent",
    label: "Consent Gap Schools",
    value: fmt(riskData.consent_gap_schools ?? 0),
    body: "Schools with more than 60% not-applied cases requiring parent consent drive.",
    cta: "Launch consent drive →",
    cardClass: "purple",
    tab: "schools",
  },
];

export const buildApaarFunnelRows = (funnelData = [], totalStudents = 1) => {
  const total = Math.max(totalStudents, 1);
  return funnelData.map((row) => {
    const count = row.count ?? 0;
    const widthPct = clamp((count / total) * 100, count > 0 ? 0.8 : 0, 100);
    return {
      status: row.status,
      count: fmt(count),
      widthPct,
      barClass: FUNNEL_BAR_CLASS[row.status] ?? "",
    };
  });
};

export const buildApaarFunnelNote = (overview = {}) => {
  const notApplied = overview.total_not_applied ?? 0;
  const failed = overview.total_failed ?? 0;
  if (notApplied >= failed * 10) {
    return "The biggest closure lever is not-applied cases. Failed records are smaller in volume but higher operational friction, so they should be routed to school-level data correction.";
  }
  return "Failed and not-applied cases both need parallel closure. Route failures to school data operators while block officers drive consent completion.";
};

export const buildApaarRatioDonuts = (overview = {}) => {
  const generation = overview.generation_rate ?? 0;
  const completion = clamp(100 - (overview.pending_pct ?? 0), 0, 100);
  const consent = clamp(100 - (overview.not_applied_pct ?? 0), 0, 100);
  return [
    {
      label: "Generation Rate",
      sub: "Generated against total students",
      pct: generation,
      display: `${generation}%`,
    },
    {
      label: "Completion",
      sub: "Operational completion indicator",
      pct: completion,
      display: `${completion}%`,
    },
    {
      label: "Consent Coverage",
      sub: "Consent-driven readiness coverage",
      pct: consent,
      display: `${consent}%`,
    },
  ];
};

export const buildApaarActionQueue = (overview = {}, riskData = {}) => {
  const consent = riskData?.consent_gap_schools ?? 0;
  const failed = overview.total_failed ?? 0;
  const requested = overview.total_requested ?? 0;
  return [
    {
      title: "Run a Parent Consent Closure Drive",
      body: `Target the ${consent} consent-gap schools first because not-applied records form the largest pending bucket.`,
      badge: "High",
      badgeClass: "amber",
      iconClass: "orange",
    },
    {
      title: "Correct Failed Records with School-Level Evidence",
      body: `Assign failed entries to school data operators and monitor closure count daily until the ${fmt(failed)} failures reduce.`,
      badge: "Critical",
      badgeClass: "",
      iconClass: "red",
    },
    {
      title: "Clear Requested-Case Backlog",
      body: `Resolve the ${fmt(requested)} requested bucket quickly to prevent old requests from becoming failed or not-applied cases.`,
      badge: "Quick win",
      badgeClass: "blue",
      iconClass: "",
    },
  ];
};

export const buildApaarMonitoringTable = (overview = {}, riskData = {}) => [
  {
    focus: "Not Applied",
    volume: fmt(overview.total_not_applied),
    risk: "Amber",
    riskClass: "",
    owner: "Block Officer",
    action: "Consent campaign",
  },
  {
    focus: "Failed",
    volume: fmt(overview.total_failed),
    risk: "Red",
    riskClass: "red",
    owner: "School Data Operator",
    action: "Data correction",
  },
  {
    focus: "Requested",
    volume: fmt(overview.total_requested),
    risk: "Blue",
    riskClass: "blue",
    owner: "APAAR Nodal Team",
    action: "Application follow-up",
  },
  {
    focus: "Critical Schools",
    volume: fmt(riskData?.critical_schools ?? 0),
    risk: "Red",
    riskClass: "red",
    owner: "Cluster Head",
    action: "Daily escalation",
  },
];

export const titleCaseBlock = (name = "") =>
  (name || "—")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const sortBlocksByPriority = (blocks = []) =>
  [...blocks].sort((a, b) => (b.priority_index ?? 0) - (a.priority_index ?? 0));

const sortBlocksByGenRate = (blocks = []) =>
  [...blocks].sort((a, b) => (b.generation_rate ?? 0) - (a.generation_rate ?? 0));

const sortBlocksByPending = (blocks = []) =>
  [...blocks].sort((a, b) => (b.pending ?? 0) - (a.pending ?? 0));

const getHbarClass = (rate = 0) => {
  if (rate < 80) return "red";
  if (rate < 85) return "amber";
  return "";
};

const getPriorityPillClass = (priority = 0) => {
  if (priority >= 10) return "high";
  if (priority >= 5) return "medium";
  return "";
};

const HIGH_ACTIONS = ["Daily backlog review", "Consent drive", "School cluster follow-up"];
const WATCH_ACTIONS = [
  "Rate improvement sprint",
  "Officer follow-up",
  "High-load schools",
  "Consent closure",
];

export const getApaarBlockStatus = (block = {}, maxGenRate = 0) => {
  const rate = block.generation_rate ?? 0;
  const priority = block.priority_index ?? 0;

  if (rate >= maxGenRate - 0.5 && rate >= 94) {
    return {
      status: "Best",
      dotClass: "",
      tagClass: "green",
      nextAction: "Replicate practice",
    };
  }
  if (rate < 80 || priority >= 10) {
    return {
      status: "High",
      dotClass: "red",
      tagClass: "red",
      nextAction: HIGH_ACTIONS[Math.min(priority >= 14 ? 0 : priority >= 10 ? 1 : 2, 2)],
    };
  }
  if (rate < 85 || priority >= 5) {
    return {
      status: "Watch",
      dotClass: "amber",
      tagClass: "",
      nextAction: WATCH_ACTIONS[Math.min(Math.floor(priority / 3), 3)],
    };
  }
  return {
    status: "On Track",
    dotClass: "",
    tagClass: "green",
    nextAction: rate >= 90 ? "Low-touch monitoring" : "Maintain cadence",
  };
};

export const buildApaarBlockGaugeStyle = (rate = 0) => {
  const pct = clamp(rate, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#ff8a1f 0 ${pct}%, rgba(255,138,31,.13) ${pct}% 100%)`,
    "--apb-gauge-color": "#ff8a1f",
  };
};

export const buildApaarBlockHeroStats = (blockData = [], overview = {}) => {
  const byGen = sortBlocksByGenRate(blockData);
  const byPending = sortBlocksByPending(blockData);
  const topGen = byGen[0];
  const lowGen = byGen[byGen.length - 1];
  const topPending = byPending[0];

  return {
    districtRate: overview.generation_rate ?? 0,
    blockCount: blockData.length,
    kpis: [
      {
        label: "Total Blocks",
        value: fmt(blockData.length),
        sub: "All blocks ranked",
        cardClass: "",
      },
      {
        label: "Top Generation Rate",
        value: `${topGen?.generation_rate ?? 0}%`,
        sub: `${titleCaseBlock(topGen?.block_name)} leads`,
        cardClass: "",
      },
      {
        label: "Largest Pending Load",
        value: fmt(topPending?.pending),
        sub: `${titleCaseBlock(topPending?.block_name)} backlog`,
        cardClass: "warning",
      },
      {
        label: "Lowest Generation Rate",
        value: `${lowGen?.generation_rate ?? 0}%`,
        sub: `${titleCaseBlock(lowGen?.block_name)} needs review`,
        cardClass: "danger",
      },
    ],
  };
};

export const buildApaarBlockStatusChips = (blockData = []) => {
  const maxGen = Math.max(...blockData.map((b) => b.generation_rate ?? 0), 0);
  const counts = { priority: 0, watch: 0, onTrack: 0 };

  blockData.forEach((block) => {
    const { status } = getApaarBlockStatus(block, maxGen);
    if (status === "High") counts.priority += 1;
    else if (status === "Watch") counts.watch += 1;
    else counts.onTrack += 1;
  });

  return [
    { label: `${counts.ontrack} on-Track Blocks`, className: "green" },
    { label: `${counts.watch} Watch Blocks`, className: "" },
    { label: `${counts.priority} Priority Blocks`, className: "blue" },
  ];
};

export const buildApaarBlockPrimaryAction = (blockData = []) => {
  const topPending = sortBlocksByPending(blockData).slice(0, 3);
  const names = topPending.map((b) => titleCaseBlock(b.block_name)).join(", ");
  return `Start with ${names || "top backlog blocks"}. Together they carry the highest pending and not-applied pressure.`;
};

export const buildApaarBlockInsightCards = (blockData = [], overview = {}) => {
  const byGen = sortBlocksByGenRate(blockData);
  const byPending = sortBlocksByPending(blockData);
  const top = byGen[0];
  const low = byGen[byGen.length - 1];
  const pendingTop = byPending[0];
  const gap = ((top?.generation_rate ?? 0) - (low?.generation_rate ?? 0)).toFixed(1);
  const districtRate = overview.generation_rate ?? 0;

  return [
    {
      id: "top",
      title: "Top Performer",
      value: titleCaseBlock(top?.block_name),
      body: `${top?.generation_rate ?? 0}% generation rate with only ${fmt(top?.pending)} pending cases.`,
      progress: top?.generation_rate ?? 0,
      progressColor: "var(--apx-success)",
      iconClass: "green",
      pill: "Best",
      pillClass: "green",
    },
    {
      id: "pending",
      title: "Highest Pending",
      value: titleCaseBlock(pendingTop?.block_name),
      body: `${fmt(pendingTop?.pending)} pending students and ${fmt(pendingTop?.total_not_applied)} not-applied cases.`,
      progress: 100,
      progressColor: "var(--apx-danger)",
      iconClass: "red",
      pill: "Priority",
      pillClass: "red",
    },
    {
      id: "low",
      title: "Lowest Rate",
      value: titleCaseBlock(low?.block_name),
      body: `${low?.generation_rate ?? 0}% generation rate, below the district average of ${districtRate}%.`,
      progress: low?.generation_rate ?? 0,
      progressColor: "var(--apx-warning)",
      iconClass: "orange",
      pill: "Watch",
      pillClass: "orange",
    },
    {
      id: "gap",
      title: "Improvement Gap",
      value: `${gap} pts`,
      body: `Spread between top performer ${titleCaseBlock(top?.block_name)} and lowest performer ${titleCaseBlock(low?.block_name)}.`,
      progress: clamp(parseFloat(gap) * 5, 8, 100),
      progressColor: "var(--apx-primary)",
      iconClass: "",
      pill: "Gap",
      pillClass: "",
    },
  ];
};

export const buildApaarBlockLeaderboard = (blockData = [], limit = 9) => {
  const sorted = sortBlocksByGenRate(blockData).slice(0, limit);
  return sorted.map((block) => ({
    name: titleCaseBlock(block.block_name),
    rate: block.generation_rate ?? 0,
    displayRate: `${block.generation_rate ?? 0}%`,
    widthPct: clamp(block.generation_rate ?? 0, 20, 100),
    barClass: getHbarClass(block.generation_rate ?? 0),
  }));
};

export const buildApaarBlockBacklogList = (blockData = [], limit = 5) => {
  const sorted = sortBlocksByPending(blockData).slice(0, limit);
  return sorted.map((block, idx) => {
    const rates = blockData.map((b) => asRate(b.generation_rate)).filter((r) => r != null);
    const minRate = rates.length ? Math.min(...rates) : null;
    const isLowestRate = asRate(block.generation_rate) != null && block.generation_rate === minRate;
    const sub = isLowestRate
      ? `${fmt(block.total_not_applied)} not applied · lowest generation rate`
      : `${fmt(block.total_not_applied)} not applied · ${block.priority_index ?? 0}% priority share`;

    return {
      rank: idx + 1,
      name: titleCaseBlock(block.block_name),
      sub,
      pending: fmt(block.pending),
    };
  });
};

export const buildApaarBlockStatusCounts = (blockData = []) => {
  const maxGen = Math.max(...blockData.map((b) => b.generation_rate ?? 0), 0);
  const counts = { high: 0, watch: 0, onTrack: 0 };

  blockData.forEach((block) => {
    const { status } = getApaarBlockStatus(block, maxGen);
    if (status === "High") counts.high += 1;
    else if (status === "Watch") counts.watch += 1;
    else counts.onTrack += 1;
  });

  return counts;
};

export const buildApaarBlockRankingTable = (blockData = []) => {
  const maxGen = Math.max(...blockData.map((b) => b.generation_rate ?? 0), 0);
  return sortBlocksByPriority(blockData).map((block, idx) => {
    const statusMeta = getApaarBlockStatus(block, maxGen);
    return {
      rank: idx + 1,
      block_name: block.block_name,
      displayName: (block.block_name || "—").toUpperCase(),
      total_schools: block.total_schools,
      total_students: fmt(block.total_students),
      total_generated: fmt(block.total_generated),
      generation_rate: `${block.generation_rate ?? 0}%`,
      pending: fmt(block.pending),
      total_not_applied: fmt(block.total_not_applied),
      priority_index: block.priority_index ?? 0,
      priorityDisplay: `${block.priority_index ?? 0}%`,
      priorityClass: getPriorityPillClass(block.priority_index ?? 0),
      ...statusMeta,
    };
  });
};

export const buildApaarBlockActions = (blockData = []) => {
  const byPending = sortBlocksByPending(blockData);
  const byGen = sortBlocksByGenRate(blockData);
  const top3 = byPending.slice(0, 3).map((b) => titleCaseBlock(b.block_name));
  const lowest = byGen[byGen.length - 1];
  const best = byGen.slice(0, 2).map((b) => titleCaseBlock(b.block_name));

  return [
    {
      title: "Close Top-3 Pending Blocks First",
      body: `${top3.join(", ") || "Top backlog blocks"} should get daily follow-up until pending volume falls below the district threshold.`,
      iconClass: "red",
    },
    {
      title: `Fix Low Conversion in ${titleCaseBlock(lowest?.block_name)}`,
      body: "Generation rate is the lowest even though total student load may be lower than top backlog blocks.",
      iconClass: "orange",
    },
    {
      title: `Replicate ${best.join(" and ") || "top block"} practices`,
      body: "Use high-performing blocks as operating model examples for consent collection and school-level tracking.",
      iconClass: "green",
    },
  ];
};

export const buildApaarBlockComparison = (blockData = [], overview = {}) => {
  const byPending = sortBlocksByPending(blockData);
  const byGen = sortBlocksByGenRate(blockData);
  const combinedPending = (byPending[0]?.pending ?? 0) + (byPending[1]?.pending ?? 0);
  const lowest = byGen[byGen.length - 1];
  const topBand = byGen.filter((b) => (b.generation_rate ?? 0) >= 95).slice(0, 3);
  const topNames = topBand.map((b) => titleCaseBlock(b.block_name)).join(", ");

  return [
    {
      title: "High-Volume, Low-Rate Risk",
      body: `${titleCaseBlock(byPending[0]?.block_name)} and ${titleCaseBlock(byPending[1]?.block_name)} have both large student base and high pending load.`,
      metric: fmt(combinedPending),
      metricLabel: "Combined pending",
    },
    {
      title: "Low-Rate Improvement Candidate",
      body: `${titleCaseBlock(lowest?.block_name)} has the lowest rate and needs targeted school-wise diagnosis.`,
      metric: `${lowest?.generation_rate ?? 0}%`,
      metricLabel: "Generation",
    },
    {
      title: "Best-Practice Replication",
      body: `${topNames || "Top blocks"} are the strongest reference blocks.`,
      metric: "95%+",
      metricLabel: "Top band",
    },
  ];
};

const shortSchoolLabel = (name = "", max = 28) => {
  const trimmed = (name || "").trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trim()}...`;
};

const getSchoolActionTag = (school = {}) => {
  const rate = school.generation_rate ?? 0;
  const pending = school.pending ?? 0;
  if (rate < 30) return { label: "Operator Support", className: "" };
  if (rate < 50) return { label: "Urgent", className: "" };
  if (rate < 65) return { label: "Consent Drive", className: "" };
  if (pending > 1200) return { label: "Follow Up", className: "amber" };
  return { label: "Verify", className: "amber" };
};

export const getApaarSchoolStatus = (school = {}) => {
  const rate = school.generation_rate ?? 0;
  const generated = school.total_generated ?? 0;

  if (generated === 0 && (school.pending ?? 0) > 0) {
    return { status: "Zero Start", tagClass: "red", nextAction: "Immediate generation drive" };
  }
  if (rate < 50 || (school.pending ?? 0) > 1600) {
    return { status: "Critical", tagClass: "red", nextAction: "Parent consent camp" };
  }
  if (rate < 80) {
    return { status: "Watch", tagClass: "", nextAction: "Data verification" };
  }
  return { status: "Monitor", tagClass: "green", nextAction: "Weekly check-in" };
};

const getSchoolNextAction = (school = {}) => {
  const meta = getApaarSchoolStatus(school);
  if (meta.status === "Zero Start") {
    if ((school.generation_rate ?? 0) < 1) return "Technical helpdesk";
    return "Block visit";
  }
  if (meta.status === "Critical") {
    if ((school.generation_rate ?? 0) < 50) return "Daily officer review";
    if ((school.generation_rate ?? 0) < 35) return "Operator support";
    return "Parent consent camp";
  }
  if (meta.status === "Watch") return "Consent follow-up";
  return meta.nextAction;
};

export const buildApaarSchoolActionGaugeStyle = (pct = 0) => {
  const load = clamp(pct, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#ef4444 0 ${load}%, rgba(239,68,68,.14) ${load}% 100%)`,
    "--aps-gauge-color": "#ef4444",
  };
};

export const buildApaarSchoolHeroStats = (topPending = [], lowPerforming = [], overview = {}) => {
  const top = topPending[0];
  const avgGen =
    topPending.length > 0
      ? topPending.reduce((sum, s) => sum + (s.generation_rate ?? 0), 0) / topPending.length
      : 0;
  const actionablePending = topPending.reduce((sum, s) => sum + (s.pending ?? 0), 0);
  const actionLoad =
    topPending.length > 0
      ? Math.round(
          topPending.reduce((sum, s) => {
            const students = s.total_student || 1;
            return sum + ((s.pending ?? 0) / students) * 100;
          }, 0) / topPending.length
        )
      : 0;

  return {
    actionLoadPct: actionLoad,
    gaugeStyle: buildApaarSchoolActionGaugeStyle(actionLoad),
    kpis: [
      {
        label: "Highest Pending School",
        value: fmt(top?.pending),
        sub: `${shortSchoolLabel(top?.school_name)} · ${titleCaseBlock(top?.block_name)}`,
        cardClass: "danger",
      },
      {
        label: "Avg Generation in Priority List",
        value: `${avgGen.toFixed(1)}%`,
        sub: `Below district rate of ${overview.generation_rate ?? 0}%`,
        cardClass: "warning",
      },
      {
        label: "Low Performing Schools",
        value: fmt(lowPerforming.length),
        sub: "Below 80% generation",
        cardClass: "purple",
      },
      {
        label: "Actionable Pending",
        value: fmt(actionablePending),
        sub: `Across top ${topPending.length} schools`,
        cardClass: "",
      },
    ],
  };
};

export const buildApaarSchoolStatusChips = (topPending = [], lowPerforming = [], blockData = []) => {
  const blocksNeedingFollowUp = blockData.filter((b) => {
    const rate = asRate(b.generation_rate);
    return (rate != null && rate < 85) || (b.priority_index ?? 0) >= 8;
  }).length;

  return [
    { label: `${topPending.length} High-Pending Schools`, className: "" },
    { label: `${lowPerforming.length} Low-Generation Schools`, className: "amber" },
    { label: `${blocksNeedingFollowUp} Blocks Need Follow-Up`, className: "blue" },
  ];
};

export const buildApaarSchoolPrimaryAction = (topPending = []) => {
  const blockCounts = {};
  topPending
    .filter((s) => (s.pending ?? 0) > 800)
    .forEach((s) => {
      const block = titleCaseBlock(s.block_name);
      blockCounts[block] = (blockCounts[block] ?? 0) + 1;
    });

  const topBlocks = Object.entries(blockCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([name]) => name);

  const blockText = topBlocks.length ? topBlocks.join(", ") : "priority blocks";
  return `Prioritize parent consent and data verification drives at ${blockText} schools with more than 800 pending records.`;
};

export const buildApaarSchoolWorkstreams = (topPending = [], lowPerforming = [], overview = {}) => {
  const consentCount = topPending.filter(
    (s) => (s.pending ?? 0) > 600 || (s.total_not_applied ?? 0) > 400
  ).length;
  const verifyCount = topPending.filter(
    (s) => (s.generation_rate ?? 0) < 70 && (s.generation_rate ?? 0) > 0
  ).length;
  const supportCount = lowPerforming.length;

  return [
    {
      id: "consent",
      title: "Parent Consent Drive",
      body: "Schools with large pending or not-applied counts need focused consent collection.",
      score: fmt(consentCount),
      scoreLabel: "schools",
      progress: clamp((consentCount / Math.max(topPending.length, 1)) * 100, 20, 100),
      progressColor: "var(--apx-danger)",
      iconClass: "red",
      dotClass: "red",
    },
    {
      id: "verify",
      title: "Data Verification",
      body: "Students generated below expected rate require demographic and class-level checks.",
      score: fmt(verifyCount),
      scoreLabel: "schools",
      progress: clamp((verifyCount / Math.max(topPending.length, 1)) * 100, 15, 90),
      progressColor: "var(--apx-secondary)",
      iconClass: "orange",
      dotClass: "amber",
    },
    {
      id: "support",
      title: "Generation Support",
      body: "Low-generation schools need operator help, upload validation and daily monitoring.",
      score: fmt(supportCount),
      scoreLabel: "schools",
      progress: clamp((supportCount / Math.max(lowPerforming.length || 1, 1)) * 80, 20, 100),
      progressColor: "var(--apx-purple)",
      iconClass: "purple",
      dotClass: "purple",
    },
    {
      id: "closure",
      title: "Officer Closure",
      body: "Track action ownership and move schools from pending to generated status.",
      score: fmt(
        overview.total_pending ??
          topPending.reduce((sum, school) => sum + (school.pending ?? 0), 0)
      ),
      scoreLabel: "pending",
      progress: clamp(overview.pending_pct ?? 0, 8, 100),
      progressColor: "var(--apx-accent)",
      iconClass: "green",
      dotClass: "",
    },
  ];
};

export const buildApaarPrioritySchoolCards = (topPending = [], limit = 5) =>
  topPending.slice(0, limit).map((school, idx) => {
    const rate = school.generation_rate ?? 0;
    const tag = getSchoolActionTag(school);
    const trackColor = rate < 50 ? "var(--apx-danger)" : rate < 70 ? "var(--apx-warning)" : "var(--apx-danger)";
    return {
      rank: idx + 1,
      school_name: school.school_name,
      udise_code: school.udise_code,
      block_code: school.block_code,
      block_name: titleCaseBlock(school.block_name),
      students: fmt(school.total_student),
      pending: fmt(school.pending),
      genRate: rate,
      trackWidth: clamp(rate, 8, 100),
      trackColor,
      actionTag: tag.label,
      actionTagClass: tag.className,
    };
  });

export const buildApaarBlockPendingStack = (topPending = [], limit = 5) => {
  const byBlock = {};
  topPending.forEach((school) => {
    const block = titleCaseBlock(school.block_name);
    byBlock[block] = (byBlock[block] ?? 0) + (school.pending ?? 0);
  });

  const fills = ["red", "orange", "purple", "", "orange"];
  const sorted = Object.entries(byBlock)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
  const max = sorted[0]?.[1] ?? 1;

  return sorted.map(([name, pending], idx) => ({
    name,
    pending: fmt(pending),
    widthPct: clamp((pending / max) * 100, 12, 100),
    fillClass: fills[idx] ?? "",
  }));
};

export const buildApaarBlockInsight = (topPending = []) => {
  const byBlock = {};
  topPending.forEach((school) => {
    const block = titleCaseBlock(school.block_name);
    byBlock[block] = (byBlock[block] ?? 0) + (school.pending ?? 0);
  });
  const top = Object.entries(byBlock).sort((a, b) => b[1] - a[1])[0];
  const blockName = top?.[0] ?? "the leading block";
  return `${blockName} alone contributes the largest pending load among priority schools. A two-day consent camp can reduce the visible backlog faster than a broad district-wide push.`;
};

export const buildApaarSchoolActionTable = (topPending = [], lowPerforming = []) => {
  const seen = new Set();
  const rows = [];

  const addSchool = (school, rank) => {
    if (!school?.udise_code || seen.has(school.udise_code)) return;
    seen.add(school.udise_code);
    const statusMeta = getApaarSchoolStatus(school);
    rows.push({
      rank,
      udise_code: school.udise_code,
      block_code: school.block_code,
      school_name: school.school_name,
      block_name: titleCaseBlock(school.block_name),
      total_student: fmt(school.total_student),
      total_generated: fmt(school.total_generated),
      pending: fmt(school.pending ?? school.total_student - (school.total_generated ?? 0)),
      generation_rate: `${school.generation_rate ?? 0}%`,
      ...statusMeta,
      nextAction: getSchoolNextAction(school),
    });
  };

  topPending.forEach((school, idx) => addSchool(school, idx + 1));
  lowPerforming
    .filter((s) => (asRate(s.generation_rate) != null && s.generation_rate < 30) || (s.total_generated ?? 0) === 0)
    .slice(0, 5)
    .forEach((school) => addSchool(school, rows.length + 1));

  return rows.slice(0, 12);
};

export const buildApaarSchoolActionQueue = (topPending = [], lowPerforming = []) => {
  const topBlocks = [...new Set(topPending.slice(0, 5).map((s) => titleCaseBlock(s.block_name)))].slice(0, 3);
  const zeroStart = lowPerforming.filter((s) => (s.total_generated ?? 0) === 0).length;
  const lowGen = lowPerforming.filter((s) => asRate(s.generation_rate) != null && s.generation_rate < 30).length;

  return [
    {
      title: "Run Consent Camps at Top Pending Schools",
      body: `Start with ${topBlocks.join(", ") || "priority blocks"} where individual school pending counts are above 800.`,
      iconClass: "red",
      tags: [
        { label: "Critical", className: "red" },
        { label: "Today", className: "" },
      ],
    },
    {
      title: "Fix Low-Generation School Operations",
      body: `Zero-start schools (${zeroStart}) should be contacted directly and monitored daily until they cross 50% generation.`,
      iconClass: "purple",
      tags: [
        { label: "Zero Start", className: "red" },
        { label: "Daily", className: "" },
      ],
    },
    {
      title: "Validate Student Records Before Generation",
      body: `Schools below 30% generation (${lowGen}) likely need data correction before volume can improve.`,
      iconClass: "orange",
      tags: [
        { label: "Verification", className: "" },
        { label: "Block Officer", className: "green" },
      ],
    },
  ];
};

const APAAR_CLASS_STAGES = [
  {
    id: "pre-primary",
    title: "Pre-Primary",
    classes: ["PP3", "PP2", "PP1"],
    iconClass: "orange",
    dotClass: "amber",
    progressColor: "var(--apx-secondary)",
  },
  {
    id: "primary",
    title: "Primary",
    classes: ["Class1", "Class2", "Class3", "Class4", "Class5"],
    iconClass: "",
    dotClass: "",
    progressColor: "var(--apx-primary)",
  },
  {
    id: "upper-primary",
    title: "Upper Primary",
    classes: ["Class6", "Class7", "Class8"],
    iconClass: "green",
    dotClass: "",
    progressColor: "var(--apx-accent)",
  },
  {
    id: "secondary",
    title: "Secondary",
    classes: ["Class9", "Class10", "Class11", "Class12"],
    iconClass: "purple",
    dotClass: "",
    progressColor: "var(--apx-purple)",
  },
];

const EARLY_GRADE_CLASSES = ["PP3", "PP2", "PP1", "Class1"];

export const formatApaarClassLabel = (cls = "") =>
  cls.startsWith("Class") ? cls.replace("Class", "Class ") : cls;

const aggregateApaarStage = (classData = [], classKeys = []) => {
  const rows = classData.filter((row) => classKeys.includes(row.class));
  const students = rows.reduce((sum, row) => sum + (row.total_students ?? 0), 0);
  const generated = rows.reduce((sum, row) => sum + (row.total_generated ?? 0), 0);
  const pending = rows.reduce((sum, row) => sum + (row.pending ?? 0), 0);
  const avgGen = students > 0 ? Math.round((generated / students) * 1000) / 10 : 0;
  return { students, generated, pending, avgGen, rows };
};

export const buildApaarClassGaugeStyle = (rate = 0) => {
  const pct = clamp(rate, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#10b981 0 ${pct}%, rgba(16,185,129,.14) ${pct}% 100%)`,
    "--apc-gauge-color": "#10b981",
  };
};

export const getApaarClassRisk = (row = {}, bestRate = 0) => {
  const rate = row.generation_rate ?? 0;
  const notAppliedPct = row.not_applied_pct ?? 0;

  if (rate === bestRate && bestRate >= 88) {
    return {
      risk: "Best",
      tagClass: "green",
      progressClass: "",
      notAppliedTagClass: "green",
      nextAction: "Replicate process",
    };
  }
  if (rate < 60 || notAppliedPct > 35) {
    return {
      risk: "Critical",
      tagClass: "red",
      progressClass: "red",
      notAppliedTagClass: "red",
      nextAction: "Immediate verification",
    };
  }
  if (rate < 70) {
    const action =
      row.class === "Class1"
        ? "Block-wise backlog drive"
        : EARLY_GRADE_CLASSES.includes(row.class)
          ? row.class === "PP2"
            ? "Parent outreach"
            : "Consent follow-up"
          : "Consent follow-up";
    return {
      risk: "Amber",
      tagClass: "",
      progressClass: "amber",
      notAppliedTagClass: "red",
      nextAction: action,
    };
  }
  if (rate < 85) {
    return {
      risk: "Watch",
      tagClass: "blue",
      progressClass: "amber",
      notAppliedTagClass: notAppliedPct > 20 ? "" : "green",
      nextAction: "Close pending list",
    };
  }
  return {
    risk: "Good",
    tagClass: "green",
    progressClass: "",
    notAppliedTagClass: notAppliedPct <= 15 ? "green" : "",
    nextAction: rate >= 88 ? "Final follow-up" : "Maintain pace",
  };
};

export const buildApaarClassHeroHeadline = (classData = []) => {
  const prePrimary = aggregateApaarStage(classData, ["PP3", "PP2", "PP1"]);
  const postPrimary = classData.filter((row) => !["PP3", "PP2", "PP1"].includes(row.class));
  const postAvg =
    postPrimary.length > 0
      ? postPrimary.reduce((sum, row) => sum + (row.generation_rate ?? 0), 0) / postPrimary.length
      : 0;

  if (prePrimary.avgGen < 65 && postAvg >= 80) {
    return "Class-wise generation is strong, but pre-primary needs focused closure";
  }
  if (prePrimary.avgGen < 70) {
    return "Early grades need targeted consent and verification before district review";
  }
  return "Class-wise APAAR generation is broadly on track across grade bands";
};

export const buildApaarClassHeroNarrative = (classData = []) => {
  const prePrimary = aggregateApaarStage(classData, ["PP3", "PP2", "PP1"]);
  const class2Plus = classData.filter((row) => !["PP3", "PP2", "PP1", "Class1"].includes(row.class));
  const stableAvg =
    class2Plus.length > 0
      ? Math.round(
          (class2Plus.reduce((sum, row) => sum + (row.generation_rate ?? 0), 0) / class2Plus.length) * 10
        ) / 10
      : 0;

  return `Classes 2–12 are mostly stable above ${Math.max(80, Math.floor(stableAvg))}%, while PP1–PP3 remain below ${Math.ceil(prePrimary.avgGen + 2)}%. This tab shifts attention from a table-first view to stage-level diagnosis and targeted action.`;
};

export const buildApaarClassHeroStats = (classData = [], overview = {}) => {
  const totalStudents = classData.reduce((sum, row) => sum + (row.total_students ?? 0), 0);
  const prePrimary = aggregateApaarStage(classData, ["PP3", "PP2", "PP1"]);
  const best = [...classData].sort((a, b) => (b.generation_rate ?? 0) - (a.generation_rate ?? 0))[0];
  const districtRate = overview.generation_rate ?? 0;
  const weightedRate =
    totalStudents > 0
      ? Math.round(
          (classData.reduce(
            (sum, row) => sum + (row.total_generated ?? 0),
            0
          ) /
            totalStudents) *
            1000
        ) / 10
      : districtRate;

  return {
    districtRate: weightedRate || districtRate,
    gaugeStyle: buildApaarClassGaugeStyle(weightedRate || districtRate),
    headline: buildApaarClassHeroHeadline(classData),
    narrative: buildApaarClassHeroNarrative(classData),
    kpis: [
      {
        label: "Total Students Analysed",
        value: formatLakh(totalStudents),
        sub: "PP3 to Class 12",
        cardClass: "primary",
      },
      {
        label: "Best Performing Class",
        value: formatApaarClassLabel(best?.class ?? "—"),
        sub: `${best?.generation_rate ?? 0}% generated`,
        cardClass: "",
      },
      {
        label: "Early-Grade Generation",
        value: `${prePrimary.avgGen}%`,
        sub: "PP1–PP3 average",
        cardClass: "warning",
      },
      {
        label: "Pre-Primary Backlog",
        value: fmt(prePrimary.pending),
        sub: "Pending in PP1–PP3",
        cardClass: "danger",
      },
    ],
  };
};

export const buildApaarClassStatusChips = (classData = [], overview = {}) => {
  const above85 = classData.filter((row) => (row.generation_rate ?? 0) >= 85).length;
  const earlyAtRisk = classData.filter(
    (row) => ["PP3", "PP2", "PP1"].includes(row.class) && (row.generation_rate ?? 0) < 65
  ).length;
  const scopeLabel =
    classData.length > 0
      ? `${classData.length} classes tracked`
      : overview.total_schools
        ? `${overview.total_schools} schools in scope`
        : "Class data loaded";

  return [
    { label: `${above85} Classes Above 85%`, className: "" },
    {
      label: `${earlyAtRisk} Early Grades at Risk`,
      className: earlyAtRisk > 0 ? "amber" : "",
    },
    { label: scopeLabel, className: "blue" },
  ];
};

export const buildApaarClassPrimaryAction = (classData = []) => {
  const prePrimary = aggregateApaarStage(classData, ["PP3", "PP2", "PP1"]);
  if (prePrimary.avgGen < 70) {
    return "Run a pre-primary consent and demographic verification drive for PP1, PP2 and PP3 before the next block-level review.";
  }
  const weak = [...classData]
    .filter((row) => (row.generation_rate ?? 0) < 80)
    .sort((a, b) => (a.generation_rate ?? 0) - (b.generation_rate ?? 0))[0];
  if (weak) {
    return `Prioritize backlog closure for ${formatApaarClassLabel(weak.class)} and adjacent early grades before the next district review.`;
  }
  return "Maintain closure momentum in higher classes while monitoring remaining pending records weekly.";
};

export const buildApaarClassStageCards = (classData = []) =>
  APAAR_CLASS_STAGES.map((stage) => {
    const stats = aggregateApaarStage(classData, stage.classes);
    return {
      ...stage,
      avgGen: stats.avgGen,
      students: fmt(stats.students),
      pending: fmt(stats.pending),
      progress: clamp(stats.avgGen, 8, 100),
    };
  });

export const buildApaarClassChartData = (classData = []) =>
  classData.map((row) => ({
    class: formatApaarClassLabel(row.class),
    classKey: row.class,
    generation_rate: row.generation_rate ?? 0,
    isEarly: ["PP3", "PP2", "PP1", "Class1"].includes(row.class),
  }));

export const buildApaarClassRiskSignals = (classData = []) => {
  if (!classData.length) return [];

  const sortedByRate = [...classData].sort(
    (a, b) => (a.generation_rate ?? 0) - (b.generation_rate ?? 0)
  );
  const sortedByPending = [...classData].sort((a, b) => (b.pending ?? 0) - (a.pending ?? 0));
  const best = [...classData].sort(
    (a, b) => (b.generation_rate ?? 0) - (a.generation_rate ?? 0)
  )[0];
  const postClass2 = classData.filter((row) =>
    ["Class3", "Class4", "Class5", "Class6", "Class7", "Class8", "Class9", "Class10", "Class11", "Class12"].includes(
      row.class
    )
  );
  const postAvg =
    postClass2.length > 0
      ? postClass2.reduce((sum, row) => sum + (row.generation_rate ?? 0), 0) / postClass2.length
      : 0;

  const lowest = sortedByRate[0];
  const heaviest = sortedByPending[0];

  return [
    {
      title: `${formatApaarClassLabel(lowest.class)} Is the Lowest Generation Class`,
      body: `Generation is ${lowest.generation_rate ?? 0}%, with ${fmt(lowest.pending)} pending records and ${lowest.not_applied_pct ?? 0}% not-applied share.`,
      iconClass: "red",
      value: "Critical",
      valueClass: "red",
    },
    {
      title: `${formatApaarClassLabel(heaviest.class)} Backlog Is Volume-Heavy`,
      body: `Even with ${heaviest.generation_rate ?? 0}% generation, ${formatApaarClassLabel(heaviest.class)} has the largest absolute pending count.`,
      iconClass: "orange",
      value: fmt(heaviest.pending),
      valueClass: "amber",
    },
    {
      title: `${formatApaarClassLabel(best.class)} Is the Best Performing Class`,
      body: `Highest generation performance at ${best.generation_rate ?? 0}% with low not-applied share.`,
      iconClass: "green",
      value: `${best.generation_rate ?? 0}%`,
      valueClass: "green",
    },
    {
      title: "Post-Class 2 Trend Is Stable",
      body: `Classes 3–12 remain largely in the ${Math.floor(postAvg - 2)}%–${Math.ceil(postAvg + 2)}% band.`,
      iconClass: "",
      value: "Stable",
      valueClass: "",
    },
  ];
};

export const buildApaarClassTableRows = (classData = []) => {
  const bestRate = Math.max(...classData.map((row) => row.generation_rate ?? 0), 0);
  return classData.map((row) => {
    const riskMeta = getApaarClassRisk(row, bestRate);
    return {
      class: formatApaarClassLabel(row.class),
      classKey: row.class,
      total_students: fmt(row.total_students),
      total_generated: fmt(row.total_generated),
      generation_rate: row.generation_rate ?? 0,
      pending: fmt(row.pending),
      not_applied_pct: `${row.not_applied_pct ?? 0}%`,
      progressWidth: clamp(row.generation_rate ?? 0, 6, 100),
      ...riskMeta,
    };
  });
};

export const buildApaarClassActionQueue = (classData = []) => {
  const class1 = classData.find((row) => row.class === "Class1");
  const best = [...classData].sort(
    (a, b) => (b.generation_rate ?? 0) - (a.generation_rate ?? 0)
  )[0];

  return [
    {
      title: "Clear Pp1–pp3 Consent Gaps First",
      body: "Assign block officers to pre-primary records with missing consent and incomplete demographic details.",
      iconClass: "red",
    },
    {
      title: "Run Class 1 Backlog Sprint",
      body: `Class 1 has the largest pending volume (${fmt(class1?.pending ?? 0)}). Create school-wise closure lists and review them daily.`,
      iconClass: "orange",
    },
    {
      title: `Replicate ${formatApaarClassLabel(best?.class ?? "Class 10")} practices`,
      body: `Use the highest-performing class process as a playbook for Classes 2, 8 and 12 where pending is still material.`,
      iconClass: "green",
    },
  ];
};

export const buildApaarInsightsGaugeStyle = (rate = 0) => {
  const pct = clamp(rate, 0, 100);
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#ff8a1f 0 ${pct}%, rgba(255,138,31,.14) ${pct}% 100%)`,
    "--apin-gauge-color": "#ff8a1f",
  };
};

export const buildApaarInsightsPrimaryInsight = (overview = {}) => {
  const pending = overview.total_pending ?? 0;
  const notApplied = overview.total_not_applied ?? 0;
  const rate = overview.generation_rate ?? 0;

  if (rate >= 80 && (pending > 0 || notApplied > 0)) {
    return "Generation is healthy overall, but the remaining backlog is driven by pending and not-applied students. Consent and school-level data verification should be handled as separate workstreams.";
  }
  if (rate < 75) {
    return "Generation needs focused district intervention. Separate consent drives, pending closure and failure correction into distinct officer-owned workstreams.";
  }
  return "APAAR generation is progressing, but pending and not-applied volumes still need daily block-level closure tracking.";
};

export const buildApaarInsightsHeroStats = (overview = {}, riskData = {}, lowPerforming = []) => {
  const rate = overview.generation_rate ?? 0;
  const priorityCount = Math.max(riskData?.critical_schools ?? 0, lowPerforming.length);

  return {
    districtRate: rate,
    gaugeStyle: buildApaarInsightsGaugeStyle(rate),
    primaryInsight: buildApaarInsightsPrimaryInsight(overview),
    statusChips: [
      { label: "AI Insight Layer", className: "purple" },
      {
        label: (overview.pending_pct ?? 0) > 10 ? "Amber Attention" : "On Track",
        className: (overview.pending_pct ?? 0) > 10 ? "" : "blue",
      },
      {
        label: `${priorityCount} Priority Schools`,
        className: "blue",
      },
      { label: `${fmt(overview.total_failed)} Failed`, className: "red" },
    ],
    kpis: [
      {
        label: "APAAR Generated",
        value: fmt(overview.total_generated),
        sub: `${rate}% completed`,
        cardClass: "",
        iconClass: "green",
      },
      {
        label: "Pending Students",
        value: fmt(overview.total_pending),
        sub: `${overview.pending_pct ?? 0}% still pending`,
        cardClass: "warn",
        iconClass: "orange",
      },
      {
        label: "Not Applied",
        value: fmt(overview.total_not_applied),
        sub: "Consent/application gap",
        cardClass: "warn",
        iconClass: "orange",
      },
      {
        label: "Failed Cases",
        value: fmt(overview.total_failed),
        sub: "Likely data quality issue",
        cardClass: "danger",
        iconClass: "red",
      },
    ],
  };
};

export const buildApaarInsightsSummaryCards = (overview = {}) => {
  const rate = overview.generation_rate ?? 0;
  const pendingPct = overview.pending_pct ?? 0;
  const notAppliedPct = overview.not_applied_pct ?? 0;
  const failedRate = overview.failure_rate_per_1000 ?? 0;

  return [
    {
      id: "stable",
      title: "Generation Rate Is Stable",
      body: `APAAR generation is at ${rate}%, which indicates the main pipeline is operational and can support targeted closure drives.`,
      severity: "Healthy",
      severityClass: "good",
      iconClass: "green",
      progress: clamp(rate, 8, 100),
      progressColor: "var(--apx-accent)",
      metaLabel: "Generated",
      metaValue: formatLakh(overview.total_generated ?? 0),
    },
    {
      id: "pending",
      title: "Pending Cases Need Daily Closure",
      body: `${formatLakh(overview.total_pending ?? 0)} students remain pending. These should be assigned to block officers with daily school-level closure tracking.`,
      severity: "Backlog",
      severityClass: "medium",
      iconClass: "orange",
      progress: clamp(pendingPct, 8, 100),
      progressColor: "var(--apx-secondary)",
      metaLabel: "Pending share",
      metaValue: `${pendingPct}%`,
    },
    {
      id: "consent",
      title: "Not-Applied Cases Are Almost Equal to Pending",
      body: `${formatLakh(overview.total_not_applied ?? 0)} students have not applied. This is likely a consent, parent outreach, or document-readiness issue rather than a technical failure.`,
      severity: "Consent Gap",
      severityClass: "medium",
      iconClass: "purple",
      progress: clamp(notAppliedPct, 8, 100),
      progressColor: "var(--apx-purple)",
      metaLabel: "Not applied",
      metaValue: `${notAppliedPct}%`,
    },
    {
      id: "failed",
      title: "Failed Cases Need Verification",
      body: `${fmt(overview.total_failed)} failures should be routed to a data correction queue for Aadhaar mismatch, duplicate record, invalid class, or student master gaps.`,
      severity: "Quality",
      severityClass: "high",
      iconClass: "red",
      progress: clamp(failedRate, 4, 100),
      progressColor: "var(--apx-danger)",
      metaLabel: "Failed rate",
      metaValue: `${failedRate}/1000`,
    },
  ];
};

export const buildApaarRootCauseSignals = (overview = {}) => {
  const notAppliedPct = overview.not_applied_pct ?? 0;
  const pendingPct = overview.pending_pct ?? 0;
  const failedRate = overview.failure_rate_per_1000 ?? 0;
  const requested = overview.total_requested ?? 0;
  const students = overview.total_students ?? 1;
  const requestedPct = (requested / students) * 100;

  const level = (score) => (score >= 70 ? "High" : score >= 40 ? "Med" : "Low");

  return [
    {
      label: "Consent Not\ncollected",
      widthPct: clamp(notAppliedPct * 5.5, 20, 95),
      barClass: "purple",
      value: level(notAppliedPct * 5.5),
    },
    {
      label: "School-Level\nfollow-Up Gap",
      widthPct: clamp(pendingPct * 5, 18, 90),
      barClass: "orange",
      value: level(pendingPct * 5),
    },
    {
      label: "Student Master\ndata Mismatch",
      widthPct: clamp(failedRate * 18, 15, 75),
      barClass: "red",
      value: level(failedRate * 18),
    },
    {
      label: "Technical Retry\nrequired",
      widthPct: clamp(requestedPct * 3.5, 12, 65),
      barClass: "",
      value: level(requestedPct * 3.5),
    },
    {
      label: "Reporting /\nupdate Delay",
      widthPct: clamp(26 + pendingPct * 0.4, 12, 40),
      barClass: "green",
      value: "Low",
    },
  ];
};

export const buildApaarAiFindings = (overview = {}) => [
  {
    title: "Pending Is the Primary Closure Lever",
    body: "Daily block-wise closure plan can move the generation rate faster than broad district-level review.",
    iconClass: "orange",
    owner: "BEO",
  },
  {
    title: "Not-Applied Should Be Run as Consent Drive",
    body: `Parent outreach and document readiness need a separate action stream for ${fmt(overview.total_not_applied)} not-applied students.`,
    iconClass: "purple",
    owner: "HM",
  },
  {
    title: "Failed Records Need Correction Queue",
    body: `Assign ${fmt(overview.total_failed)} failures to data operators with reason codes and retry status.`,
    iconClass: "red",
    owner: "Data Cell",
  },
];

export const buildApaarPriorityWatchlist = (lowPerforming = [], riskData = {}) => {
  const fromLow = [...lowPerforming]
    .sort((a, b) => (a.generation_rate ?? 0) - (b.generation_rate ?? 0))
    .slice(0, 3);

  const schools =
    fromLow.length >= 3
      ? fromLow
      : [
          ...fromLow,
          ...(riskData?.critical_list ?? []).map((s) => ({
            school_name: s.school_name,
            block_name: s.block_name,
            generation_rate: s.gen_rate,
          })),
        ].slice(0, 3);

  return schools.map((school) => {
    const rate = school.generation_rate ?? 0;
    let note = "verification pending";
    if (rate === 0) note = "generation not started";
    else if (rate < 5) note = "very low generation";

    return {
      school_name: school.school_name,
      block_name: titleCaseBlock(school.block_name),
      note,
      generation_rate: `${rate}%`,
      udise_code: school.udise_code,
    };
  });
};

export const buildApaarInsightsActionQueue = (overview = {}, riskData = {}, lowPerforming = []) => {
  const notApplied = overview.total_not_applied ?? 0;
  const pending = overview.total_pending ?? 0;
  const failed = overview.total_failed ?? 0;
  const lowCount = lowPerforming.length || (riskData?.critical_schools ?? 0);

  return [
    {
      title: "Run Consent Closure Campaign",
      body:
        notApplied > pending * 0.6
          ? `${fmt(notApplied)} not-applied students exceed 60% of the pending backlog.`
          : `Track ${fmt(notApplied)} not-applied students against ${fmt(pending)} pending cases.`,
      iconClass: "purple",
    },
    {
      title: "Create School-Wise Pending Owner List",
      body: `Assign owners for ${lowCount} low-performing or critical school${lowCount === 1 ? "" : "s"} in the current scope.`,
      iconClass: "orange",
    },
    {
      title: "Separate Failure Correction Queue",
      body: `Route ${fmt(failed)} failed records to a dedicated correction queue before mixing with consent backlog.`,
      iconClass: "red",
    },
  ];
};

export const buildApaarInsightTrackerRows = (overview = {}, riskData = {}, lowPerforming = []) => {
  const lowCount = Math.max(lowPerforming.length, riskData?.critical_schools ?? 0);
  return [
    {
      priority: 1,
      insight: "Consent / not-applied gap",
      evidence: `${fmt(overview.total_not_applied)} not applied`,
      owner: "Headmaster + Cluster Officer",
      action: "Parent outreach, consent collection, document readiness camp",
      status: "Critical",
      tagClass: "red",
    },
    {
      priority: 2,
      insight: "Pending backlog",
      evidence: `${fmt(overview.total_pending)} pending`,
      owner: "Block Education Officer",
      action: "Daily block-wise closure target and owner review",
      status: "High",
      tagClass: "",
    },
    {
      priority: 3,
      insight: "Failed records",
      evidence: `${fmt(overview.total_failed)} failed`,
      owner: "Data Cell",
      action: "Classify failure reason, correct master data, retry generation",
      status: "High",
      tagClass: "",
    },
    {
      priority: 4,
      insight: "Low-performing schools",
      evidence: lowCount > 0 ? `${lowCount} schools flagged` : "No schools flagged",
      owner: "Cluster Officer",
      action: "Visit school, verify records, and submit closure report",
      status: "Watchlist",
      tagClass: "blue",
    },
    {
      priority: 5,
      insight: "District generation performance",
      evidence: `${overview.generation_rate ?? 0}% generated`,
      owner: "District Nodal Officer",
      action: "Maintain dashboard review cadence and publish daily movement",
      status: "Stable",
      tagClass: "green",
    },
  ];
};

export const buildApaarInsightsMarkdown = (
  overview = {},
  riskData = {},
  lowPerforming = []
) => {
  if (!overview?.total_students) {
    return [
      "## Insights",
      "- No APAAR data available for the current filters.",
      "",
      "## Root Cause Signals",
      "- Missing or incomplete records in the selected scope.",
      "",
      "## Recommendations",
      "- Refresh data or broaden filters to include valid records.",
      "",
      "## Priority Action Items",
      "- Validate APAAR data import for the selected scope.",
    ].join("\n");
  }

  const primary = buildApaarInsightsPrimaryInsight(overview);
  const summaryCards = buildApaarInsightsSummaryCards(overview);
  const rootSignals = buildApaarRootCauseSignals(overview);
  const findings = buildApaarAiFindings(overview);
  const actions = buildApaarInsightsActionQueue(overview, riskData, lowPerforming);
  const tracker = buildApaarInsightTrackerRows(overview, riskData, lowPerforming);

  return [
    "## Insights",
    `- ${primary}`,
    ...summaryCards.map((c) => `- ${c.title}: ${c.body}`),
    "",
    "## Root Cause Signals",
    ...rootSignals.map((s) => `- ${s.label.replace(/\n/g, " ")} (${s.value})`),
    ...findings.map((f) => `- ${f.title}: ${f.body}`),
    "",
    "## Recommendations",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    ...tracker.slice(0, 4).map((r) => `- ${r.insight}: ${r.action} (${r.owner})`),
  ].join("\n");
};

export const buildApaarExecutiveMarkdown = (overview = {}, riskData = {}) => {
  if (!overview?.total_students) {
    return [
      "## Insights",
      "- No APAAR executive data for the current scope.",
      "",
      "## Priority Action Items",
      "- Load APAAR data before generating executive actions.",
    ].join("\n");
  }

  const queue = buildApaarActionQueue(overview, riskData);
  const workstreams = buildApaarWorkstreams(overview);

  return [
    "## Insights",
    `- ${buildApaarHeroHeadline(overview)}`,
    `- ${buildApaarHeroNarrative(overview)}`,
    `- ${buildApaarFunnelNote(overview)}`,
    "",
    "## Root Cause Signals",
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Recommendations",
    ...workstreams.map((w) => `- ${w.title}: ${w.body}`),
    "",
    "## Priority Action Items",
    `- ${buildApaarPrimaryAction(overview, riskData)}`,
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
  ].join("\n");
};

export const buildApaarWorkstreams = (overview = {}) => {
  const notApplied = overview.total_not_applied ?? 0;
  const failed = overview.total_failed ?? 0;
  const requested = overview.total_requested ?? 0;
  const generated = overview.total_generated ?? 0;
  const pending = overview.total_pending ?? 0;

  return [
    {
      title: "Consent Collection",
      body: `Track ${fmt(notApplied)} not-applied and ${fmt(pending)} pending consent cases across schools.`,
      owner: "School",
      sla: notApplied > 1000 ? "5 days" : "7 days",
      iconClass: "orange",
    },
    {
      title: "Failure Resolution",
      body: `Validate and correct ${fmt(failed)} failed records before re-submission.`,
      owner: "Data Operator",
      sla: failed > 500 ? "2 days" : "3 days",
      iconClass: "red",
    },
    {
      title: "Requested Cases",
      body: `Monitor ${fmt(requested)} requested APAAR IDs and prevent aging beyond review threshold.`,
      owner: "Nodal Team",
      sla: requested > 200 ? "1 day" : "2 days",
      iconClass: "",
    },
    {
      title: "Quality Review",
      body: `Audit ${fmt(generated)} generated IDs and verify school-wise closure quality.`,
      owner: "District MIS",
      sla: "Weekly",
      iconClass: "green",
    },
  ];
};

export const buildApaarSchoolActionMarkdown = (
  overview = {},
  topPending = [],
  lowPerforming = [],
  blockData = []
) => {
  const primary = buildApaarSchoolPrimaryAction(topPending);
  const workstreams = buildApaarSchoolWorkstreams(topPending, lowPerforming, overview);
  const queue = buildApaarSchoolActionQueue(topPending, lowPerforming);

  return [
    "## Insights",
    `- ${primary}`,
    `- ${topPending.length} high-pending schools and ${lowPerforming.length} low-generation schools need differentiated action lanes.`,
    ...workstreams.map((w) => `- ${w.title}: ${w.score} ${w.scoreLabel}`),
    "",
    "## Root Cause Signals",
    `- Top pending blocks: ${[...new Set(topPending.slice(0, 5).map((s) => s.block_name))].slice(0, 3).join(", ") || "—"}.`,
    `- ${lowPerforming.filter((s) => (s.total_generated ?? 0) === 0).length} zero-start schools in scope.`,
    "",
    "## Recommendations",
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
    `- Monitor ${blockData.length || "district"} blocks with daily school-wise exception lists.`,
  ].join("\n");
};

export const buildApaarClassMarkdown = (overview = {}, classData = []) => {
  const hero = buildApaarClassHeroStats(classData, overview);
  const primary = buildApaarClassPrimaryAction(classData);
  const signals = buildApaarClassRiskSignals(classData);
  const queue = buildApaarClassActionQueue(classData);

  return [
    "## Insights",
    `- ${hero.headline}: ${hero.narrative}`,
    `- District generation rate ${hero.districtRate}% across class stages.`,
    "",
    "## Root Cause Signals",
    ...signals.map((s) => `- ${s.title}: ${s.body}`),
    "",
    "## Recommendations",
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${primary}`,
    ...queue.map((a) => `- ${a.title}: ${a.body}`),
  ].join("\n");
};
